import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { Select, CheckySelect } from '../../../Shared/Form'
import { DROPDOWN_OPTIONS, CHECKBOX_GROUPS } from '../constants'
import HouseIcon from '../../../../../../assets/images/icons/house-icon.svg'
import LandIcon from '../../../../../../assets/images/icons/land-icon.svg'
import TownhouseIcon from '../../../../../../assets/images/icons/townhouse-icon.svg'
import CondoIcon from '../../../../../../assets/images/icons/condo-icon.svg'
import CoopIcon from '../../../../../../assets/images/icons/coop-icon.svg'
import FarmIcon from '../../../../../../assets/images/icons/farm-icon.svg'
import MultifamilyIcon from '../../../../../../assets/images/icons/multifamily-icon.svg'
import NewConstructionIcon from '../../../../../../assets/images/icons/new-construction-icon.svg'
import SingleStoryIcon from '../../../../../../assets/images/icons/single-story-icon.svg'
import SouthHighwayIcon from '../../../../../../assets/images/icons/south-highway-icon.svg'
import PoolIcon from '../../../../../../assets/images/icons/pool-icon.svg'
import TennisIcon from '../../../../../../assets/images/icons/tennis-icon.svg'
import SaunaIcon from '../../../../../../assets/images/icons/sauna-icon.svg'
import HotTubIcon from '../../../../../../assets/images/icons/hot-tub-icon.svg'
import LinensIcon from '../../../../../../assets/images/icons/linens-icon.svg'
import CentralACIcon from '../../../../../../assets/images/icons/central-ac-icon.svg'
import SecuritySystemIcon from '../../../../../../assets/images/icons/security-system-icon.svg'
import BasementIcon from '../../../../../../assets/images/icons/basement-icon.svg'
import TVCableIcon from '../../../../../../assets/images/icons/tv-cable-icon.svg'
import HandicapIcon from '../../../../../../assets/images/icons/handicap-icon.svg'
import GymIcon from '../../../../../../assets/images/icons/gym-icon.svg'
import LaundryIcon from '../../../../../../assets/images/icons/laundry-icon.svg'
import FireplaceIcon from '../../../../../../assets/images/icons/fireplace-icon.svg'
import VideoIcon from '../../../../../../assets/images/icons/video-icon.svg'
import ThreeDTourIcon from '../../../../../../assets/images/icons/3d-tour-icon.svg'
import PetsAllowedIcon from '../../../../../../assets/images/icons/pets-allowed-icon.svg'
import SmokingAllowedIcon from '../../../../../../assets/images/icons/smoking-allowed-icon.svg'
import WaterAccessIcon from '../../../../../../assets/images/icons/water-access-icon.svg'
import OutdoorShowerIcon from '../../../../../../assets/images/icons/outdoor-shower-icon.svg'
import BeachRightsIcon from '../../../../../../assets/images/icons/beach-rights-icon.svg'
import DockIcon from '../../../../../../assets/images/icons/dock-icon.svg'
import SprinklerIcon from '../../../../../../assets/images/icons/sprinkler-icon.svg'
import GuestHouseIcon from '../../../../../../assets/images/icons/guest-house-icon.svg'

const WATER_VIEW_AMENITIES = [
  'Oceanfront', 'Oceanview', 'Waterfront', 'Waterview',
  'Bayfront', 'Bayview', 'Pondfront', 'Pondview',
  'Harborfront', 'Harborview', 'Lakefront', 'Lakeview',
  'Creek/Inletfront', 'Creek/Inletview', 'Canalfront', 'Canalview',
  'Soundfront', 'Soundview'
]
const PARKING_OPTIONS = ['Any', '1+', '2+', '3+', '4+', '5+']
const VIRTUAL_TOURS = [
  { icon: VideoIcon, label: 'Video', field: 'videoTour' },
  { icon: ThreeDTourIcon, label: '3D tour', field: 'zillow3dTour' }
]

const PROPERTY_TYPE_ICONS = {
  'single-family': HouseIcon,
  'townhouse/townhome': TownhouseIcon,
  condo: CondoIcon,
  'co-op': CoopIcon,
  farm: FarmIcon,
  'multi-family': MultifamilyIcon,
  land: LandIcon
}

const CHECKBOX_ICONS = {
  'new-construction': NewConstructionIcon,
  'single-story': SingleStoryIcon,
  'water-access': WaterAccessIcon,
  'south-highway': SouthHighwayIcon,
  pool: PoolIcon,
  tennis: TennisIcon,
  sauna: SaunaIcon,
  'hot-tub': HotTubIcon,
  linens: LinensIcon,
  'central-ac': CentralACIcon,
  'security-system': SecuritySystemIcon,
  basement: BasementIcon,
  'tv-cable': TVCableIcon,
  handicap: HandicapIcon,
  gym: GymIcon,
  laundry: LaundryIcon,
  fireplace: FireplaceIcon,
  'pets-allowed': PetsAllowedIcon,
  'smoking-allowed': SmokingAllowedIcon,
  'outdoor-shower': OutdoorShowerIcon,
  'beach-rights': BeachRightsIcon,
  dock: DockIcon,
  sprinkler: SprinklerIcon,
  'guest-house': GuestHouseIcon
}

const PROPERTY_TYPES = [
  { id: 'single-family', label: 'Single-family' },
  { id: 'townhouse/townhome', label: 'Townhouse' },
  { id: 'condo', label: 'Condo' },
  { id: 'co-op', label: 'Co-op' },
  { id: 'farm', label: 'Farm' },
  { id: 'multi-family', label: 'Multifamily' },
  { id: 'land', label: 'Land' }
]

const VirtualTourButton = ({ icon, label, field, isSelected, onClick }) => (
  <button
    className={classNames('PropertyDetails-virtualTourButton', {
      'PropertyDetails-virtualTourButton--selected': isSelected
    })}
    onClick={onClick}
  >
    <img src={icon} alt='' className='PropertyDetails-virtualTourIcon' />
    {label}
  </button>
)

VirtualTourButton.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
}

const getPostedByValue = (byAgent, byOwner) => {
  if (byAgent && byOwner) return 'any'
  if (byAgent) return 'agent'
  if (byOwner) return 'owner'
  return 'any'
}

const handlePostedByChange = (value, onDropdownChange) => {
  const actualValue = typeof value === 'object' && value !== null ? value.id : value
  const updates = {
    any: { byAgent: false, byOwner: false },
    agent: { byAgent: true, byOwner: false },
    owner: { byAgent: false, byOwner: true }
  }
  const { byAgent, byOwner } = updates[actualValue] || updates.any
  onDropdownChange('byAgent', byAgent)
  onDropdownChange('byOwner', byOwner)
}

const DropdownField = ({ label, minField, maxField, options, valueStateStore, onDropdownChange, single = false }) => {
  const handleChange = useCallback((field, value) => {
    const actualValue = typeof value === 'object' && value !== null ? value.id : value
    if (actualValue === 'no-min' || actualValue === 'no-max' || actualValue === 'any') {
      onDropdownChange(field, null)
      return
    }
    const numericValue = !isNaN(actualValue) && actualValue !== null ? Number(actualValue) : actualValue
    onDropdownChange(field, numericValue)
  }, [onDropdownChange])

  const commonSelectProps = useMemo(() => ({
    className: 'PropertyDetails-dropdown',
    optionIdentity: option => option.id,
    optionTemplate: option => option.label,
    selectedOptionTemplate: option => option.label
  }), [])

  const getValue = useCallback(field => {
    const value = valueStateStore.values[field]
    return value != null ? String(value) : null
  }, [valueStateStore.values])

  const renderSelect = useCallback((field, selectOptions, placeholder, idSuffix = '') => (
    <Select
      {...commonSelectProps}
      id={`${field}${idSuffix}`}
      name={field}
      options={selectOptions}
      value={getValue(field)}
      placeholder={placeholder}
      onChange={value => handleChange(field, value)}
    />
  ), [commonSelectProps, getValue, handleChange])

  return (
    <div className='PropertyDetails-fieldRow'>
      <label className='PropertyDetails-label'>{label}</label>
      <div className={single ? 'PropertyDetails-singleDropdown' : 'PropertyDetails-dropdowns'}>
        {single ? (
          renderSelect(minField, options, options[0]?.label)
        ) : (
          <>
            {renderSelect(minField, options.filter(opt => opt.id !== 'no-max'), 'No Min', '-min')}
            {renderSelect(maxField, options.filter(opt => opt.id !== 'no-min'), 'No Max', '-max')}
          </>
        )}
      </div>
    </div>
  )
}

const CheckboxGroup = ({ checkboxes, valueStateStore, onCheckboxChange }) => (
  <div className='PropertyDetails-checkboxGroup'>
    {checkboxes.map(({ name, label, icon }) => (
      <div key={name} className='PropertyDetails-checkboxWrapper'>
        <label className='PropertyDetails-checkbox'>
          <input
            type='checkbox'
            className='PropertyDetails-checkboxInput'
            name={name}
            checked={valueStateStore.values[name] || false}
            onChange={({ target }) => onCheckboxChange(name, target.checked)}
          />
          {icon && <img src={CHECKBOX_ICONS[icon]} alt='' className='PropertyDetails-checkboxIcon' />}
          <span className='PropertyDetails-checkboxLabel'>{label}</span>
        </label>
      </div>
    ))}
  </div>
)

const StandardCheckboxGroup = ({ checkboxes, valueStateStore, onCheckboxChange }) => (
  <div className='PropertyDetails-standardCheckboxGroup'>
    {checkboxes.map(({ name, label }) => (
      <div key={name} className='PropertyDetails-standardCheckboxWrapper'>
        <label className='PropertyDetails-standardCheckbox'>
          <input
            type='checkbox'
            className='PropertyDetails-standardCheckboxInput'
            name={name}
            checked={valueStateStore.values[name] || false}
            onChange={({ target }) => onCheckboxChange(name, target.checked)}
          />
          <span className='PropertyDetails-standardCheckboxLabel'>{label}</span>
        </label>
      </div>
    ))}
  </div>
)

const AmenitySubsection = ({ title, checkboxes, valueStateStore, onCheckboxChange, standard = false }) => (
  <div className='PropertyDetails-subsection'>
    <h4 className='PropertyDetails-subsectionTitle'>{title}</h4>
    {standard ? (
      <StandardCheckboxGroup
        checkboxes={checkboxes}
        valueStateStore={valueStateStore}
        onCheckboxChange={onCheckboxChange}
      />
    ) : (
      <CheckboxGroup
        checkboxes={checkboxes}
        valueStateStore={valueStateStore}
        onCheckboxChange={onCheckboxChange}
      />
    )}
  </div>
)

const ShowMoreLink = ({ isExpanded, onClick }) => (
  <div className='PropertyDetails-showMore'>
    <a href='#' className='PropertyDetails-showMoreLink' onClick={onClick}>
      {isExpanded ? 'Show less ↑' : 'Show more ↓'}
    </a>
  </div>
)

const PropertyDetails = ({ valueStateStore, uiStateStore, listingTypeStore, onDropdownChange, onCheckboxChange }) => {
  const propertyStyles = uiStateStore?.propertyStyles || []
  const unitTypesCollection = uiStateStore?.unitTypesCollection || []
  const isForSale = listingTypeStore?.current === 'sales'
  const isForRent = listingTypeStore?.current === 'rentals'

  const [showPropertyTypes, setShowPropertyTypes] = useState(false)
  const [showAmenities, setShowAmenities] = useState(false)

  const prevIsLandRef = useRef(false)

  const unitTypes = valueStateStore.values.unitTypes
  const amenityNames = valueStateStore.values.amenityNames
  const isLand = useMemo(() => unitTypes?.includes('land'), [unitTypes])

  useEffect(() => {
    const propertyTypeSelections = unitTypes?.filter(type => type !== 'land') || []
    const hasSelectedPropertyTypes = propertyTypeSelections.length > 0

    if (hasSelectedPropertyTypes && !showPropertyTypes) {
      setShowPropertyTypes(true)
    }

    if (isLand && !prevIsLandRef.current) {
      setShowPropertyTypes(false)
    }

    prevIsLandRef.current = isLand
  }, [unitTypes, isLand, showPropertyTypes])

  useEffect(() => {
    const hiddenAmenityNames = [
      ...CHECKBOX_GROUPS.indoorAmenities.map(a => a.name),
      ...CHECKBOX_GROUPS.outdoorAmenities.map(a => a.name),
      ...CHECKBOX_GROUPS.roomAmenities.map(a => a.name),
      ...CHECKBOX_GROUPS.communityAmenities.map(a => a.name)
    ]
    const selectedHiddenAmenities = amenityNames?.filter(name => hiddenAmenityNames.includes(name)) || []
    const hasSelectedHiddenAmenities = selectedHiddenAmenities.length > 0
    const hasCarSpaces = valueStateStore.values.carSpaces && valueStateStore.values.carSpaces !== 'Any'

    if ((hasSelectedHiddenAmenities || hasCarSpaces || (isLand && !prevIsLandRef.current)) && !showAmenities) {
      setShowAmenities(true)
    }
  }, [amenityNames?.length, isLand, showAmenities, valueStateStore.values.carSpaces])

  const basicAmenities = useMemo(() => {
    let amenities = isForRent ? CHECKBOX_GROUPS.basicAmenities : CHECKBOX_GROUPS.basicAmenities.filter(a => a.name !== 'Linens')

    if (isLand) {
      amenities = amenities.filter(a => !['Pool', 'Tennis', 'Sauna', 'Hot Tub'].includes(a.name))
    }

    return amenities
  }, [isForRent, isLand])

  const filteredOutdoorAmenities = useMemo(() =>
    CHECKBOX_GROUPS.outdoorAmenities.filter(a =>
      !['Outdoor Shower(s)', 'Sprinkler System', 'Pool/Guest House'].includes(a.name)
    ),
    []
  )

  const listingStatusOptions = useMemo(() => (
    isForRent ? DROPDOWN_OPTIONS.rentalStatus : DROPDOWN_OPTIONS.listingStatus
  ), [isForRent])

  const handleToggle = useCallback((setter, current) => e => {
    e.preventDefault()
    e.stopPropagation()
    setter(!current)
  }, [])

  const handleWaterViewChange = useCallback(value => {
    const actualValue = typeof value === 'object' && value !== null ? value.id : value
    WATER_VIEW_AMENITIES.forEach(amenity => {
      if (valueStateStore.values[amenity]) onDropdownChange(amenity, false)
    })
    if (actualValue !== 'any') onDropdownChange(actualValue, true)
  }, [onDropdownChange, valueStateStore.values])

  return (
    <div className='PropertyDetails'>
      <div className='PropertyDetails-section'>
        <h3 className='PropertyDetails-sectionTitle'>Property Details</h3>

        {isForSale && (
        <div className='PropertyDetails-fieldRow'>
          <label className='PropertyDetails-label'>Property type</label>
          <div className='PropertyDetails-propertyTypeButtons'>
            <button
              className={`PropertyDetails-propertyTypeToggle ${
                !isLand ? 'PropertyDetails-propertyTypeToggle--selected' : ''
              }`}
              onClick={() => {
                const currentTypes = unitTypes || []
                if (currentTypes.includes('land')) {
                  const newTypes = currentTypes.filter(t => t !== 'land')
                  onCheckboxChange('unitTypes', newTypes)
                }
              }}
            >
              <span className='PropertyDetails-propertyTypeToggleIcon'>
                <img src={HouseIcon} alt='' />
              </span>
              <span className='PropertyDetails-propertyTypeToggleLabel'>House</span>
            </button>
            <button
              className={`PropertyDetails-propertyTypeToggle ${
                isLand ? 'PropertyDetails-propertyTypeToggle--selected' : ''
              }`}
              onClick={() => {
                onCheckboxChange('unitTypes', ['land'])
              }}
            >
              <span className='PropertyDetails-propertyTypeToggleIcon'>
                <img src={LandIcon} alt='' />
              </span>
              <span className='PropertyDetails-propertyTypeToggleLabel'>Land</span>
            </button>
          </div>
        </div>
        )}

        {!isLand && (
          <DropdownField
            label='Square feet'
            minField='sqftFrom'
            maxField='sqftTo'
            options={DROPDOWN_OPTIONS.squareFeet}
            valueStateStore={valueStateStore}
            onDropdownChange={onDropdownChange}
          />
        )}

        <DropdownField
          label='Lot size'
          minField='acreageFrom'
          maxField='acreageTo'
          options={DROPDOWN_OPTIONS.lotSize}
          valueStateStore={valueStateStore}
          onDropdownChange={onDropdownChange}
        />

        {!isLand && (
          <DropdownField
            label='Year built'
            minField='yearFrom'
            maxField='yearTo'
            options={DROPDOWN_OPTIONS.yearBuilt}
            valueStateStore={valueStateStore}
            onDropdownChange={onDropdownChange}
          />
        )}

        <div className='PropertyDetails-fieldRow'>
          <label className='PropertyDetails-label'>Water view</label>
          <div className='PropertyDetails-singleDropdown'>
            <Select
              key={`waterView-${WATER_VIEW_AMENITIES.find(a => valueStateStore.values[a]) || 'any'}`}
              className='PropertyDetails-dropdown'
              id='waterView'
              name='waterView'
              options={DROPDOWN_OPTIONS.waterView}
              optionIdentity={option => option.id}
              optionTemplate={option => option.label}
              selectedOptionTemplate={option => option.label}
              value={WATER_VIEW_AMENITIES.find(amenity => valueStateStore.values[amenity]) || 'any'}
              placeholder='Any'
              onChange={handleWaterViewChange}
            />
          </div>
        </div>

        {!isLand && (
          <div className='PropertyDetails-fieldRow'>
            <label className='PropertyDetails-label'>Property style</label>
            <div className='PropertyDetails-singleDropdown'>
              <CheckySelect
                className='PropertyDetails-dropdown'
                id='propertyStyle'
                name='propertyStyle'
                options={propertyStyles}
                optionIdentity={option => option.id}
                optionTemplate={option => option.label}
                selectedOptionsTemplate={options => options.map(o => o.label).join(', ')}
                value={valueStateStore.values.styles || []}
                placeholder='Any'
                withClearAll
                onChange={selectedOptions => {
                  const styleIds = selectedOptions.map(opt => opt.id)
                  onDropdownChange('styles', styleIds)
                }}
              />
            </div>
          </div>
        )}

        {showPropertyTypes && !isLand && (
          <div className='PropertyDetails-propertyTypeSection'>
            <div className='PropertyDetails-propertyTypeGrid'>
              {PROPERTY_TYPES.filter(({ id }) => id !== 'land').map(({ id, label }) => {
                const unitTypes = valueStateStore.values.unitTypes
                const isSelected = unitTypes && unitTypes.includes && unitTypes.includes(id)

                return (
                  <div
                    key={id}
                    className={`PropertyDetails-propertyTypeButton ${isSelected ? 'PropertyDetails-propertyTypeButton--selected' : ''}`}
                    onClick={() => {
                      const currentUnitTypes = unitTypes && unitTypes.slice ? unitTypes.slice() : []
                      const newUnitTypes = isSelected
                        ? currentUnitTypes.filter(typeId => typeId !== id)
                        : [...currentUnitTypes, id]
                      onCheckboxChange('unitTypes', newUnitTypes)
                    }}
                  >
                    <img src={PROPERTY_TYPE_ICONS[id]} alt='' className='PropertyDetails-propertyTypeIcon' />
                    <span className='PropertyDetails-propertyTypeLabel'>{label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <CheckboxGroup
          checkboxes={
            isLand
              ? CHECKBOX_GROUPS.propertyType.filter(checkbox => checkbox.name !== 'singleStory')
              : CHECKBOX_GROUPS.propertyType
          }
          valueStateStore={valueStateStore}
          onCheckboxChange={onCheckboxChange}
        />

        {!isLand && (
          <ShowMoreLink
            isExpanded={showPropertyTypes}
            onClick={handleToggle(setShowPropertyTypes, showPropertyTypes)}
          />
        )}
      </div>

      <div className='PropertyDetails-section'>
        <h3 className='PropertyDetails-sectionTitle'>Amenities</h3>

        {!isLand && (
          <CheckboxGroup
            checkboxes={basicAmenities}
            valueStateStore={valueStateStore}
            onCheckboxChange={onCheckboxChange}
          />
        )}

        {isLand && (
          <CheckboxGroup
            checkboxes={filteredOutdoorAmenities}
            valueStateStore={valueStateStore}
            onCheckboxChange={onCheckboxChange}
          />
        )}

        {!isLand && showAmenities && (
          <>
            <AmenitySubsection
              title='Indoors'
              checkboxes={CHECKBOX_GROUPS.indoorAmenities}
              valueStateStore={valueStateStore}
              onCheckboxChange={onCheckboxChange}
            />

            <AmenitySubsection
              title='Outdoors'
              checkboxes={CHECKBOX_GROUPS.outdoorAmenities}
              valueStateStore={valueStateStore}
              onCheckboxChange={onCheckboxChange}
            />

            <AmenitySubsection
              title='Rooms'
              checkboxes={CHECKBOX_GROUPS.roomAmenities}
              valueStateStore={valueStateStore}
              onCheckboxChange={onCheckboxChange}
              standard
            />

            <AmenitySubsection
              title='Community'
              checkboxes={CHECKBOX_GROUPS.communityAmenities}
              valueStateStore={valueStateStore}
              onCheckboxChange={onCheckboxChange}
              standard
            />

            <div className='PropertyDetails-subsection'>
              <h4 className='PropertyDetails-subsectionTitle'>Parking spaces</h4>
              <div className='PropertyDetails-parkingButtons'>
                {PARKING_OPTIONS.map(label => {
                  const carSpaces = valueStateStore.values.carSpaces
                  const normalizeValue = (val) => val?.toString().replace('+', '') || ''
                  const isSelected = label === 'Any'
                    ? !carSpaces
                    : normalizeValue(carSpaces) === normalizeValue(label)
                  return (
                    <button
                      key={label}
                      className={classNames('PropertyDetails-parkingButton', {
                        'PropertyDetails-parkingButton--selected': isSelected
                      })}
                      onClick={() => {
                        const newValue = label === 'Any' ? null : label
                        onDropdownChange('carSpaces', newValue)
                      }}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
          </>
        )}

        {!isLand && (
          <ShowMoreLink
            isExpanded={showAmenities}
            onClick={handleToggle(setShowAmenities, showAmenities)}
          />
        )}
      </div>

      {isForRent && (
        <div className='PropertyDetails-section'>
          <h3 className='PropertyDetails-sectionTitle'>House Rules</h3>
          <CheckboxGroup
            checkboxes={CHECKBOX_GROUPS.houseRules}
            valueStateStore={valueStateStore}
            onCheckboxChange={onCheckboxChange}
          />
        </div>
      )}

      <div className='PropertyDetails-section'>
        <h3 className='PropertyDetails-sectionTitle'>Listing Details</h3>

        <div className='PropertyDetails-fieldRow'>
          <label className='PropertyDetails-label'>Listing status</label>
          <div className='PropertyDetails-singleDropdown'>
            <CheckySelect
              className='PropertyDetails-dropdown'
              id='listingStatus'
              name='listingStatus'
              options={listingStatusOptions}
              optionIdentity={option => option.id}
              optionTemplate={option => option.label}
              selectedOptionsTemplate={options => options.map(o => o.label).join(', ')}
              value={valueStateStore.values.status || []}
              withClearAll
              onChange={selectedOptions => {
                const statusIds = selectedOptions.map(opt => opt.id)
                onDropdownChange('status', statusIds)
              }}
            />
          </div>
        </div>

        <DropdownField
          label='Open house'
          minField='openHouse'
          options={DROPDOWN_OPTIONS.openHouse}
          valueStateStore={valueStateStore}
          onDropdownChange={onDropdownChange}
          single
        />

        {isForSale && !isLand && (
          <DropdownField
            label='Price per sq.ft.'
            minField='pricePerSqftFrom'
            maxField='pricePerSqftTo'
            options={DROPDOWN_OPTIONS.pricePerSqFt}
            valueStateStore={valueStateStore}
            onDropdownChange={onDropdownChange}
          />
        )}

        <div className='PropertyDetails-fieldRow'>
          <label className='PropertyDetails-label'>Posted by</label>
          <div className='PropertyDetails-singleDropdown'>
            <Select
              className='PropertyDetails-dropdown'
              id='postedBy'
              name='postedBy'
              options={DROPDOWN_OPTIONS.postedBy}
              optionIdentity={option => option.id}
              optionTemplate={option => option.label}
              selectedOptionTemplate={option => option.label}
              value={getPostedByValue(valueStateStore.values.byAgent, valueStateStore.values.byOwner)}
              placeholder='Any'
              onChange={value => handlePostedByChange(value, onDropdownChange)}
            />
          </div>
        </div>

        <div className='PropertyDetails-fieldRow'>
          <label className='PropertyDetails-label'>Virtual tours</label>
          <div className='PropertyDetails-virtualTours'>
            {VIRTUAL_TOURS.map(({ icon, label, field }) => (
              <VirtualTourButton
                key={field}
                icon={icon}
                label={label}
                field={field}
                isSelected={!!valueStateStore.values[field]}
                onClick={() => onDropdownChange(field, !valueStateStore.values[field])}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

AmenitySubsection.propTypes = {
  title: PropTypes.string.isRequired,
  checkboxes: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string
  })).isRequired,
  valueStateStore: PropTypes.shape({
    values: PropTypes.object.isRequired
  }).isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  standard: PropTypes.bool
}

ShowMoreLink.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
}

const checkboxGroupPropTypes = {
  checkboxes: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string
  })).isRequired,
  valueStateStore: PropTypes.shape({
    values: PropTypes.object.isRequired
  }).isRequired,
  onCheckboxChange: PropTypes.func.isRequired
}

CheckboxGroup.propTypes = checkboxGroupPropTypes
StandardCheckboxGroup.propTypes = checkboxGroupPropTypes

DropdownField.propTypes = {
  label: PropTypes.string.isRequired,
  minField: PropTypes.string.isRequired,
  maxField: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  valueStateStore: PropTypes.shape({
    values: PropTypes.object.isRequired
  }).isRequired,
  onDropdownChange: PropTypes.func.isRequired,
  single: PropTypes.bool
}

PropertyDetails.propTypes = {
  valueStateStore: PropTypes.shape({
    values: PropTypes.object.isRequired
  }).isRequired,
  uiStateStore: PropTypes.shape({
    propertyStyles: PropTypes.array
  }),
  listingTypeStore: PropTypes.shape({
    current: PropTypes.string
  }),
  onDropdownChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired
}

export default observer(PropertyDetails)
